import NiceModal from '@ebay/nice-modal-react';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterContextProvider, RouterProvider } from '@tanstack/react-router';
import { StrictMode, type ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { WithCaptureURLState } from './CaptureURLState.tsx';
import { Toaster } from './components/ui/sonner.tsx';
import { TooltipProvider } from './components/ui/tooltip.tsx';
import { PostHog } from './Posthog.tsx';
import { router } from './router';
import { GlobalStoreProvider } from './stores/global-store.provider.tsx';
import { useGlobalStore } from './stores/global.store.ts';
import { TitleProvider, applyStoredBrandTitle } from './stores/title-context.tsx';
import { useModalContextBridge } from './utils/createModal.tsx';
import { LinguiProvider } from './utils/locale.tsx';
import { queryClient } from './utils/reactQueryClient.ts';
import { ThemeProvider } from './utils/theme-provider.tsx';
import { VendorAnalytics } from './utils/vendor-analytics.ts';

// Modals portal outside the router tree, so Link and useNavigate inside them need the router bridged in.
const bridgeRouter = (children: ReactNode) => <RouterContextProvider router={router}>{children}</RouterContextProvider>;

const RouterWithGlobalStore = () => {
	const { store } = useGlobalStore();
	useModalContextBridge(bridgeRouter);
	return <RouterProvider router={router} context={{ globalStore: store }} />;
};

window.addEventListener('vite:preloadError', (e: Event) => {
	console.log('vite preload error', e);
	window.location.reload();
});

const bootstrap = async () => {
	// Before the first paint: a white-label host shows its own remembered name while hydrate resolves
	// the brand, instead of the platform's.
	applyStoredBrandTitle();

	if (import.meta.env.PROD) {
		VendorAnalytics.initAll();
	}

	if (!import.meta.env.DEV) {
		Sentry.init({
			dsn: import.meta.env.VITE_APP_SENTRY_DSN,
			environment: import.meta.env.VITE_APP_ENV,
			integrations: [],
			beforeSend(event) {
				const exceptionValue = event.exception?.values?.[0]?.value ?? '';
				const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
				const hasThirdPartyAutofill =
					exceptionValue.includes('setContactAutofillValuesFromBridge') ||
					frames.some((f) => f.function?.includes('setContactAutofillValuesFromBridge'));

				if (hasThirdPartyAutofill) {
					return null;
				}

				return event;
			},
		});
	}

	const root = ReactDOM.createRoot(document.getElementById('root')!);

	root.render(
		<StrictMode>
			<PostHog>
				<WithCaptureURLState>
					<ThemeProvider defaultTheme="light" storageKey="vadio-ui-theme">
						<TooltipProvider>
							<QueryClientProvider client={queryClient}>
								<GlobalStoreProvider>
									{/* LinguiProvider reads language from the global store, so it must live inside GlobalStoreProvider. */}
									<LinguiProvider>
										<TitleProvider>
											<NiceModal.Provider>
												<RouterWithGlobalStore />
											</NiceModal.Provider>
											<Toaster richColors />
										</TitleProvider>
									</LinguiProvider>
								</GlobalStoreProvider>
							</QueryClientProvider>
						</TooltipProvider>
					</ThemeProvider>
				</WithCaptureURLState>
			</PostHog>
		</StrictMode>,
	);
};

bootstrap();
